import React, { FC, useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  Button,
  IconButton,
  TextField,
  Alert,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { BRAND, BRAND_DARKER, formatDate } from './healthWalletConstants';
import {
  UserDocument,
  useReprocessUserDocumentMutation,
  useDeleteUserDocumentMutation,
  useRenameUserDocumentMutation,
  useLazyGetUserDocumentContentQuery,
  useGetOnboardingStatusQuery,
  healthWalletApi,
} from './healthWalletSlice';
import { useAppDispatch } from '../../app/hooks';
import { UploadDocumentButton } from './UploadDocumentButton';

interface UserDocumentRowProps {
  doc: UserDocument;
  // Refetch the list after retry/replace so status flips.
  onChanged: () => void;
  // Start expanded (e.g. the first document in the list opens automatically).
  defaultExpanded?: boolean;
}

const isImage = (mime: string) => mime.startsWith('image/');
const isText = (mime: string) => mime === 'text/plain';

/**
 * One uploaded document — inline-expandable to view (USER_DOCUMENTS_UI_PLAN.md §4/§6).
 * Collapsed: name + date pill + status, with an inline pencil to rename in place. Expanded:
 * lazily fetches the content (rung 2) and renders image / text / PDF inline; failed docs offer
 * Retry; all offer Replace. Everything is vertically stacked for mobile.
 */
export const UserDocumentRow: FC<UserDocumentRowProps> = ({ doc, onChanged, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [reprocess, { isLoading: isRetrying }] = useReprocessUserDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteUserDocumentMutation();
  const [renameDocument, { isLoading: isRenaming }] = useRenameUserDocumentMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(doc.document_name || '');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [fetchContent, { data: content, isFetching: isContentLoading, error: contentError }] =
    useLazyGetUserDocumentContentQuery();
  const dispatch = useAppDispatch();

  // Auto-open rows (e.g. the first document) start expanded but never fire handleToggle, so kick off the
  // lazy content fetch on mount. Runs once; later expands go through handleToggle as usual.
  useEffect(() => {
    if (expanded && !content) fetchContent({ document_id: doc.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry is disabled while any health-data activity (IAS/Epic sync or another extraction) holds the
  // shared per-patient lock — same shared signal that disables upload and the retrieve buttons.
  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const isDataRequestPending =
    onboardingStatus?.dataRequestStatus === 'pending' || onboardingStatus?.dataRequestStatus === 'processing';

  const status = doc.status;
  const isProcessing = status === 'pending' || status === 'processing';
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';

  // In-place description edit (collapsed-row pencil → inline field). stopPropagation everywhere so
  // editing never toggles the accordion; it's pure metadata, so we just refetch the list on success.
  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftName(doc.document_name || '');
    setRenameError(null);
    setEditing(true);
  };

  const cancelEdit = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    setEditing(false);
    setRenameError(null);
  };

  const saveEdit = async (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    const name = draftName.trim();
    if (!name) {
      setRenameError('Description cannot be empty.');
      return;
    }
    if (name === (doc.document_name || '')) {
      setEditing(false);
      return;
    }
    setRenameError(null);
    try {
      await renameDocument({ document_id: doc.id, description: name }).unwrap();
      setEditing(false);
      onChanged();
    } catch (err: any) {
      if (err?.status === 409 || err?.data?.error === 'data_activity_in_progress') {
        setRenameError('A health data update is in progress. Try again shortly.');
      } else {
        setRenameError('Could not update the description. Please try again.');
      }
    }
  };

  const handleToggle = (_e: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    // Lazy content fetch on first expand only (cached after).
    if (isExpanded && !content) {
      fetchContent({ document_id: doc.id });
    }
  };

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await reprocess({ document_id: doc.id }).unwrap();
      // NO optimistic dataRequestStatus patch (same reasoning as UploadDocumentButton): the extraction's
      // ias_request doesn't exist yet at response time, and a status-only patch with a stale id forges
      // the SSE snapshot merge's race signature. The drain's first SSE progress event flips the real
      // status; the advisory lock guards double-runs meanwhile. See project_hw_seed_optimistic_interaction.
    } catch {
      /* surfaced by the list status on refetch */
    } finally {
      onChanged();
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Two-step confirm (same pattern as account delete) — first click arms, second deletes.
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleteError(null);
    try {
      await deleteDocument({ document_id: doc.id }).unwrap();
      // No mutation tag (consistent with the feature): manually re-retrieve the summary via the
      // Patient tag, and refetch the list so the row disappears. See USER_DATA_DESIGN.md.
      dispatch(healthWalletApi.util.invalidateTags(['Patient']));
      onChanged();
    } catch (err: any) {
      if (err?.status === 409 || err?.data?.error === 'data_activity_in_progress') {
        setDeleteError('A health data update is in progress. Please try again in a few minutes.');
      } else {
        setDeleteError(err?.data?.message || err?.message || 'Could not delete this document.');
      }
      setConfirmDelete(false);
    }
  };

  const dataUrl =
    content && content.content_base64
      ? `data:${content.mime_type};base64,${content.content_base64}`
      : null;

  const renderContent = () => {
    if (isContentLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: BRAND_DARKER }} />
        </Box>
      );
    }
    if (contentError || !dataUrl) {
      return (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Could not load this document.
        </Alert>
      );
    }
    if (isImage(content!.mime_type)) {
      return (
        <Box
          component="img"
          src={dataUrl}
          alt={doc.document_name}
          sx={{ width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 2, bgcolor: '#f8fafc' }}
        />
      );
    }
    if (isText(content!.mime_type)) {
      let text = '';
      try {
        text = atob(content!.content_base64);
      } catch {
        text = '(unable to decode text)';
      }
      return (
        <Box
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            m: 0,
            p: 1.5,
            maxHeight: 360,
            overflowY: 'auto',
            bgcolor: '#f8fafc',
            borderRadius: 2,
            fontSize: '0.8rem',
          }}
        >
          {text}
        </Box>
      );
    }
    // PDF — in-page iframes are unreliable on mobile; offer Open / Download.
    return (
      <Button
        component={Link}
        href={dataUrl}
        target="_blank"
        rel="noopener"
        download={`${doc.document_name || 'document'}.pdf`}
        variant="outlined"
        startIcon={<OpenInNewIcon />}
        sx={{ textTransform: 'none', borderRadius: 50, borderColor: BRAND_DARKER, color: BRAND_DARKER }}
      >
        Open document
      </Button>
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggle}
      disableGutters
      sx={{ boxShadow: 'none', border: '1px solid #d7d3f0', borderRadius: 2, '&:before': { display: 'none' }, mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
          {editing ? (
            // Inline description edit — stopPropagation on click/keydown so the accordion never toggles while editing.
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                autoFocus
                size="small"
                fullWidth
                value={draftName}
                error={!!renameError}
                helperText={renameError || undefined}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                  if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                }}
                disabled={isRenaming}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_DARKER },
                }}
              />
              <IconButton size="small" onClick={saveEdit} disabled={isRenaming} aria-label="Save description" sx={{ color: '#16a34a' }}>
                {isRenaming ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={cancelEdit} disabled={isRenaming} aria-label="Cancel edit" sx={{ color: '#475569' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <Chip
                size="small"
                label={formatDate(doc.received_at)}
                sx={{ bgcolor: BRAND, color: BRAND_DARKER, fontWeight: 600, flexShrink: 0 }}
              />
              <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#1c1c1e', flex: 1, minWidth: 0 }}>
                {doc.document_name || 'Untitled document'}
              </Typography>
              <Tooltip title={isDataRequestPending ? 'Unavailable while a health data update is in progress' : 'Edit description'}>
                {/* span wrapper so the Tooltip still shows when the button is disabled */}
                <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={startEdit}
                    disabled={isDataRequestPending}
                    aria-label="Edit description"
                    sx={{ color: BRAND_DARKER }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {isProcessing && (
                <Chip
                  size="small"
                  icon={<CircularProgress size={12} sx={{ color: BRAND_DARKER }} />}
                  label="Processing"
                  sx={{ bgcolor: '#efeaff', color: BRAND_DARKER, fontWeight: 600, flexShrink: 0 }}
                />
              )}
              {isCompleted && <CheckCircleIcon fontSize="small" sx={{ color: '#16a34a', flexShrink: 0 }} />}
              {isFailed && (
                <Tooltip title={doc.processing_error || 'Extraction failed'}>
                  <ErrorIcon fontSize="small" sx={{ color: '#dc2626', flexShrink: 0 }} />
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {isFailed && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {doc.processing_error || 'We could not extract data from this document.'}
            </Alert>
          )}

          {renderContent()}

          {/* Actions — Replace + Delete on ONE row to save vertical space (Retry joins it; wraps on narrow). */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {isFailed && (
              <Button
                size="small"
                variant="outlined"
                startIcon={isRetrying ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRetry}
                disabled={isRetrying || isDataRequestPending}
                sx={{ textTransform: 'none', borderRadius: 50, borderColor: BRAND_DARKER, color: BRAND_DARKER }}
              >
                Retry extraction
              </Button>
            )}
            <UploadDocumentButton replaceOrgId={doc.org_id} label="Replace" onUploaded={onChanged} />

            {/* Delete — two-step confirm; disabled while any activity is in progress (also 409-guarded server-side). */}
            {!confirmDelete ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDelete}
                disabled={isDeleting || isDataRequestPending}
                sx={{ textTransform: 'none', borderRadius: 50, color: '#dc2626', borderColor: '#dc2626' }}
              >
                Delete
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
                  onClick={handleDelete}
                  disabled={isDeleting || isDataRequestPending}
                  sx={{ textTransform: 'none', borderRadius: 50, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                >
                  Confirm delete
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                  disabled={isDeleting}
                  sx={{ textTransform: 'none', color: '#475569' }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          {deleteError && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
